/**
 * Where somebody is.
 *
 * The page leads with what is in the way. A screen that opens with a wall of
 * history makes the reader do the work of finding the one course that is
 * stuck; this one puts that first and the history underneath, because the
 * question being asked is almost always "is this candidate ready, and if not
 * why not" — not "what has happened".
 *
 * The same page serves a trainee looking at themselves and a coordinator
 * looking at a candidate. It says whose it is rather than quietly changing.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type ProgressReport, type CourseProgress, type TimelineEvent } from '../api';

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const since = (iso: string | null) => {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return day(iso);
};

export function Progress() {
  const routeId = useParams().userId;
  const [report, setReport] = useState<ProgressReport | null>(null);
  const [own, setOwn] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get<{ report: ProgressReport; own: boolean }>(
        routeId ? `/progress/${routeId}` : '/progress',
      );
      setReport(res.report); setOwn(res.own);
    } catch (e) { setError((e as ApiError).message); }
    finally { setLoading(false); }
  }, [routeId]);
  useEffect(() => { void load(); }, [load]);

  if (loading) return <><h1>Progress</h1><p className="muted">Loading…</p></>;
  if (error) return <><h1>Progress</h1><p className="auth__error">{error}</p></>;
  if (!report) return <><h1>Progress</h1><p className="muted">Nothing to show.</p></>;

  const { person, summary, courses, timeline } = report;
  const stuck = courses.filter((c) => c.blocker);
  // A blocked course stays in the table as well as in the panel above. The
  // panel leads with the problem; the table is where you see how far through
  // they got and when they last touched it, which is exactly what you want
  // once you know something is stuck.
  const going = courses.filter((c) => c.status !== 'completed');
  const done = courses.filter((c) => c.status === 'completed');

  return (
    <>
      {!own && <p className="crumbs"><Link to="/admin/people">People</Link></p>}
      <h1>{own ? 'My progress' : `${person.firstName} ${person.lastName ?? ''}`.trim()}</h1>

      <div className="facts facts--wide">
        <Fact n={summary.completed} of={summary.courses} label="courses finished" />
        {summary.blocked > 0 && <Fact n={summary.blocked} label="stuck" bad />}
        {summary.overdue > 0 && <Fact n={summary.overdue} label="overdue" bad />}
        <Fact n={summary.certificates} label="certificates" />
        <div className="fact">
          <strong>{since(summary.lastActiveAt)}</strong>
          <span className="muted small">last worked on something</span>
        </div>
      </div>

      {/* What is in the way, first, because it is the reason anybody opened
          this page. */}
      {stuck.length > 0 && (
        <section className="panel">
          <h2 className="h2">In the way</h2>
          <ul className="blockers">
            {stuck.map((c) => (
              <li key={c.courseId}>
                <strong>{c.courseName}</strong>
                <div className={`blocker blocker--${c.blocker!.kind}`}>{c.blocker!.text}</div>
                {c.blocker!.kind === 'awaiting_marking' && !own && (
                  <Link className="small" to="/marking">Mark it</Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {courses.length === 0 && (
        <p className="muted">
          {own ? 'You are not on any courses yet.' : 'They are not on any courses yet.'}
        </p>
      )}

      {going.length > 0 && (
        <>
          <h2 className="h2">Under way</h2>
          <CourseTable rows={going} own={own} />
        </>
      )}

      {done.length > 0 && (
        <>
          <h2 className="h2">Finished</h2>
          <CourseTable rows={done} own={own} />
        </>
      )}

      {timeline.length > 0 && (
        <>
          <h2 className="h2">What happened</h2>
          <ol className="timeline">
            {timeline.map((e, i) => <Event key={`${e.at}-${i}`} e={e} />)}
          </ol>
        </>
      )}
    </>
  );
}

function Fact({ n, of, label, bad }: { n: number; of?: number; label: string; bad?: boolean }) {
  return (
    <div className="fact">
      <strong className={bad ? 'warn' : undefined}>{of != null ? `${n} of ${of}` : n}</strong>
      <span className="muted small">{label}</span>
    </div>
  );
}

function CourseTable({ rows, own }: { rows: CourseProgress[]; own: boolean }) {
  return (
    <table className="grid">
      <thead>
        <tr><th>Course</th><th>Done</th><th>Score</th><th>Next</th><th>Last opened</th></tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.courseId}>
            <td>
              {own
                ? <Link to={`/courses/${c.courseId}`}>{c.courseName}</Link>
                : <Link to={`/admin/courses/${c.courseId}`}>{c.courseName}</Link>}
              {c.overdue && <div className="warn small">Overdue since {day(c.expiresAt)}</div>}
            </td>
            <td className="num">
              {c.unitsDone}/{c.unitsTotal}
              <div className="bar bar--sm" aria-hidden="true">
                <span style={{ width: `${c.completionPct}%` }} />
              </div>
            </td>
            <td className="num">{c.score == null ? '' : `${Math.round(c.score)}%`}</td>
            <td className="small">
              {c.status === 'completed'
                ? <span className="muted">finished {day(c.completedAt)}</span>
                : c.blocker
                  // Same colour as the panel above: amber when somebody here
                  // has to act, red when the trainee has hit a wall.
                  ? <span className={`blocker blocker--${c.blocker.kind}`}>{c.blocker.text}</span>
                  : c.nextUnit?.name ?? <span className="muted">—</span>}
            </td>
            <td className="small muted">{since(c.lastAccessedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const MARK: Record<TimelineEvent['kind'], string> = {
  enrolled: '·', unit: '✓', test: '✓', assignment: '↑', marked: '✎',
  class: '◆', certificate: '★', completed: '★',
};

function Event({ e }: { e: TimelineEvent }) {
  return (
    <li className={`timeline__item timeline__item--${e.kind}`}>
      <span className="timeline__mark" aria-hidden="true">{MARK[e.kind]}</span>
      <span className="timeline__when">{day(e.at)}</span>
      <span className="timeline__what">
        {e.title}
        {e.detail && <span className="muted small"> · {e.detail}</span>}
      </span>
    </li>
  );
}
